/**
 * A agenda de uma implantação — a tela 6 do protótipo, do lado do console.
 *
 * **Ela não promete calendário.** A medição de 25/08 não encontrou uma linha de uso programático
 * do Google Calendar na organização, nem service account com delegação de domínio. O modo
 * `calendar` existe no contrato e o banco recusa gravá-lo sem o id do evento externo — então ele
 * **não aparece como opção**, nem desabilitada: um controle que devolve `422` ensina que o portal
 * está quebrado, e não que a integração não foi construída. O que aparece é a dívida por escrito,
 * na tela em que alguém procuraria o botão que não existe, e o único caminho que funciona —
 * marcar no próprio calendário e registrar aqui o horário e o link.
 *
 * **A agenda tem rota própria, e não é uma aba da implantação.** Duas razões: uma reunião é um
 * endereço para o qual se volta (o consultor abre a ata dela semanas depois), e a tela da
 * implantação já reúne cinco fontes — acrescentar a agenda inteira ali reconstruiria dentro de uma
 * página a dispersão que ela veio desfazer.
 *
 * **As pendências das atas ficam aqui, e não na tela da reunião.** Elas atravessam reuniões: a
 * pendência que saiu do kickoff é cobrada na de acompanhamento, e quem pergunta "o que ficou em
 * aberto com este cliente" não quer abrir uma reunião de cada vez.
 */

import { useState } from "react";
import { Link, useParams } from "react-router";
import { Aviso, Botao } from "../componentes/ui";
import {
  FormularioDaReuniao,
  ListaDeReunioes,
  PendenciasDaAta,
} from "../componentes/reuniao";
import { useTitulo } from "../nucleo/titulo";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { caminhoDaImplantacao, mensagemDoConsole, useEsteiraDaImplantacao } from "../nucleo/console";
import {
  PERMISSAO_DE_AGENDAR,
  PERMISSAO_DE_EDITAR_ATA,
  PERMISSAO_DE_VER_ATA,
  caminhoDaReuniao,
  useConcluirPendencia,
  usePendenciasDaAta,
  usePendenciasGeoTask,
  useReativarGeoTask,
  useReunioes,
} from "../nucleo/reuniao";

export function ReunioesDaImplantacao() {
  const { id } = useParams();
  const implantacaoId = id ?? "";
  const sessao = useSessao();
  const esteira = useEsteiraDaImplantacao(implantacaoId);
  const podeAgendar = podeNaSessao(sessao.data, PERMISSAO_DE_AGENDAR);
  const podeVerAta = podeNaSessao(sessao.data, PERMISSAO_DE_VER_ATA);
  const reunioes = useReunioes(podeVerAta ? implantacaoId : "");
  const pendencias = usePendenciasDaAta(implantacaoId, podeVerAta);
  const pendenciasGeoTask = usePendenciasGeoTask(
    implantacaoId, podeNaSessao(sessao.data, "console:ver"),
  );
  const podeEditarAta = podeNaSessao(sessao.data, PERMISSAO_DE_EDITAR_ATA);
  const reativar = useReativarGeoTask(implantacaoId);
  const abandonadas = pendenciasGeoTask.data?.abandonadas ?? 0;
  const conclusao = useConcluirPendencia();
  const [marcando, setMarcando] = useState(false);

  useTitulo("Reuniões de Implantação");

  const nome =
    esteira.data?.organizacao.nome_fantasia ?? esteira.data?.organizacao.razao_social ?? null;

  return (
    <div data-testid="agenda-da-implantacao">
      <div className="cabecalho-da-trilha">
        <div>
          <h1 className="titulo-pagina">Reuniões de Implantação</h1>
          <p className="lead mb-2">
            {nome ? <>{nome} · </> : null}O horário, a gravação e a ata de cada conversa com este
            cliente.
          </p>
        </div>
      </div>

      {!podeVerAta ? (
        <Aviso tom="marca" data-testid="sem-ata-ver">
          <b>O seu acesso não inclui as reuniões e as atas.</b> Peça a quem administra os
          acessos do portal.
        </Aviso>
      ) : (
        <>
          {pendenciasGeoTask.isError ? (
            <Aviso tom="erro" className="mb-3" data-testid="erro-das-pendencias-geotask">
              Não foi possível consultar as pendências do GeoTask. {mensagemDoConsole(pendenciasGeoTask.error)}
              <Botao variante="fantasma" onClick={() => void pendenciasGeoTask.refetch()}>
                Tentar de novo
              </Botao>
            </Aviso>
          ) : null}
          {pendenciasGeoTask.data?.pendencias.map((pendencia, indice) => (
            <Aviso tom="atencao" className="mb-3" key={`${indice}-${pendencia}`}>
              {pendencia}
            </Aviso>
          ))}
          {abandonadas > 0 && podeAgendar ? (
            <div className="acoes-do-console mb-3" data-testid="reativar-geotask">
              <Botao
                variante="fantasma"
                disabled={reativar.isPending}
                onClick={() => reativar.mutate()}
              >
                {reativar.isPending
                  ? "Enviando de novo…"
                  : abandonadas === 1
                    ? "Tentar de novo a operação parada"
                    : `Tentar de novo as ${abandonadas} operações paradas`}
              </Botao>
              {reativar.isError ? (
                <span role="alert">{mensagemDoConsole(reativar.error)}</span>
              ) : null}
            </div>
          ) : null}
          <section className="mb-6" aria-labelledby="titulo-da-agenda">
            <h2 className="titulo-secao" id="titulo-da-agenda">
              Agenda
            </h2>

            {/* A dívida escrita onde ela é procurada: nesta tela, por quem veio buscar o botão
                que não existe. Não é rodapé nem `title` de ícone — é a explicação de por que o
                portal não marca sozinho, e ela fica acima do formulário porque é a primeira
                pergunta de quem chega aqui. */}
            <Aviso tom="marca" className="mb-4" data-testid="calendario-indisponivel">
              Marque a reunião no seu calendário, como você já faz, e registre aqui o horário e o
              link: é assim que ela entra na esteira do cliente e ganha uma ata.
            </Aviso>

            {podeAgendar ? (
              marcando ? (
                <FormularioDaReuniao
                  implantacaoId={implantacaoId}
                  modulos={esteira.data?.modulos}
                  aoConcluir={() => setMarcando(false)}
                />
              ) : (
                <div className="acoes-do-console mb-3">
                  <Botao onClick={() => setMarcando(true)} data-testid="marcar-reuniao">
                    Registrar uma reunião
                  </Botao>
                </div>
              )
            ) : (
              <p className="nota-rodape mb-3" data-testid="sem-reuniao-agendar">
                Registrar reunião fica com quem agenda as reuniões.
              </p>
            )}

            {reunioes.isPending ? (
              <p className="lead" role="status">
                Carregando as reuniões…
              </p>
            ) : reunioes.isError ? (
              <Aviso tom="erro" data-testid="falha-das-reunioes">
                {mensagemDoConsole(reunioes.error)}
              </Aviso>
            ) : reunioes.data.total === 0 ? (
              <Aviso tom="marca" data-testid="sem-reunioes">
                <b>Nenhuma reunião registrada para este cliente.</b> Marque a reunião no seu
                calendário, como você já faz, e registre aqui o horário e o link — é assim que ela
                entra na esteira do cliente e ganha uma ata.
              </Aviso>
            ) : (
              <ListaDeReunioes
                reunioes={reunioes.data.reunioes}
                caminhoDe={(reuniao) => caminhoDaReuniao(implantacaoId, reuniao.id)}
              />
            )}
          </section>

          <section className="mb-6" aria-labelledby="titulo-das-pendencias-de-ata">
            <h2 className="titulo-secao" id="titulo-das-pendencias-de-ata">
              Pendências das atas
            </h2>
            {pendencias.isPending ? (
              <p className="lead" role="status">
                Carregando as pendências…
              </p>
            ) : pendencias.isError ? (
              <Aviso tom="erro" data-testid="falha-das-pendencias">
                {mensagemDoConsole(pendencias.error)}
              </Aviso>
            ) : (
              <>
                {conclusao.isError ? (
                  <Aviso tom="erro" data-testid="falha-da-conclusao">
                    {mensagemDoConsole(conclusao.error)}
                  </Aviso>
                ) : null}
                <PendenciasDaAta
                  pendencias={pendencias.data}
                  publico="interno"
                  /* Só quem edita a ata fecha a pendência: sem a permissão o botão não existe,
                     em vez de existir e devolver 403. */
                  aoConcluir={
                    podeEditarAta
                      ? (pendencia) =>
                          conclusao.mutate({
                            ataId: pendencia.ata_id,
                            itemId: pendencia.id,
                          })
                      : undefined
                  }
                  concluindo={conclusao.isPending ? conclusao.variables.itemId : null}
                />
              </>
            )}
          </section>
        </>
      )}

      <p className="nota-rodape mt-5">
        <Link to={caminhoDaImplantacao(implantacaoId, "reunioes")}>Voltar à implantação</Link>
      </p>
    </div>
  );
}
